import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';
import { AnimatePresence } from 'framer-motion';

import { CloseMenu } from '@webapp/store/ui/action-creators';

import { Ownership } from '@services/ownership/types/ownership';
import { SortableHandle, SortableElement } from 'react-sortable-hoc';

import Menu from '@components/menu';
import Flex from '@components/layout/flex';
import InputField from '@components/form/input';
import CloseIcon from '@components/basics/icons/close';
import Typography from '@components/basics/typography';
import HandleIcon from '@components/basics/icons/handle';
import DotsCircleIcon from '@components/basics/icons/dots-circle';

interface OwnershipRowCtrlProps {
  className?: string;
  owner: Ownership;
  locked?: boolean;
  request?: boolean;
  leftDecoration?: any;
  isRequest?: boolean;
  removedByUser?: string;
  userId?: string;
  givePercentUsername?: string;
  lockedPeriod?: boolean;
  ownerShip: Ownership[];
  withoutGiveMessage?: boolean;
  onRemove: () => void;
  closeMenu: () => CloseMenu;
  updateCurrentSplit: (newSplit: number) => void;
  requestNewSplit: (newSplit: number) => void;
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  closeMenu: () => dispatch(new CloseMenu()),
});

const Handle = SortableHandle(styled((props: { className?: string }) => (
  <HandleIcon {...props} />
))`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 18px;
  height: 18px;
  z-index: 4;

  ${({ theme }) => `@media screen and ${theme.device.laptopXL}`} {
    width: 24px;
    height: 24px;
  }
`);

function parseName(owner: Ownership) {
  let type = '';

  switch (owner.ownerType) {
    case 'ARTIST': {
      type = 'Collaborator';
      break;
    }
    case 'RECORD_LABEL': {
      type = 'Record Label';
      break;
    }
    case 'USER': {
      type = 'User';
      break;
    }
    case 'MANAGER': {
      type = 'Manager';
      break;
    }
  }

  return `${owner.name || owner.contact || ''} (${type})`;
}

const getSplitValue = (owner: Ownership, splitFocus?: boolean) => {
  if (owner.split) {
    return String(owner.split);
  }
  return splitFocus ? '' : '0';
};

const getNewSplitValue = (owner: Ownership, splitFocus?: boolean) => {
  if (owner.requestedSplit) {
    return String(owner.requestedSplit);
  }
  return splitFocus ? '' : '0';
};

export const OwnershipRowCtrl: React.FC<OwnershipRowCtrlProps> = ({
  className,
  locked,
  owner,
  isRequest,
  request,
  removedByUser,
  userId,
  leftDecoration,
  withoutGiveMessage,
  givePercentUsername,
  requestNewSplit,
  closeMenu,
  onRemove,
  updateCurrentSplit,
  ...props
}) => {
  const lockedPeriod = false;
  const [isActive, setActive] = React.useState(false);
  const [isMenuActive, setMenuActive] = React.useState(false);
  const [splitFocus, setSplitFocus] = React.useState(false);
  const [originalSplit, setOriginalSplit] = React.useState(
    getSplitValue(owner, splitFocus)
  );
  const [newSplit, setNewSplit] = React.useState(
    owner.requestedSplit
      ? String(owner.requestedSplit)
      : getSplitValue(owner, splitFocus)
  );

  React.useEffect(() => {
    if (owner) {
      setOriginalSplit(getSplitValue(owner));
    }
  }, [owner]);

  const handleChangeCondition = (value: string) => {
    return (
      !value ||
      (value.match(/^[0-9]*\.?[0-9]*$/) &&
        Number(value) <= 100 &&
        Number(value) >= 0)
    );
  };
  return (
    <OwnershipRowCtrlContainer
      className={className || ''}
      onMouseOver={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
    >
      <AnimatePresence exitBeforeEnter>
        {!(request || lockedPeriod) && isActive && (
          <Handle className="handle-icon" />
        )}
      </AnimatePresence>
      <Flex>
        <InputField
          style={{
            width:
              owner.requestedSplit || (!lockedPeriod && request && !locked)
                ? '50%'
                : '80%',
          }}
          className={`input-split with-decoration ${
            isMenuActive ? 'parent-menu-open' : ''
          }`}
          label={
            owner.ownershipType === 'WRITER' ? (
              <Translate id="RELEASE.TRACK_INFO.TRACK.OWNERSHIP.INPUT.SONGWRITER_NAME" />
            ) : (
              <Translate id="RELEASE.TRACK_INFO.TRACK.OWNERSHIP.INPUT.OWNER_NAME" />
            )
          }
          value={parseName(owner)}
          locked={
            locked ||
            owner.ownerType === 'MANAGER' ||
            (lockedPeriod && !!owner.id && owner.ownerId !== userId)
          }
          rightDecoration={
            owner.ownerType === 'MANAGER' ||
            (props.lockedPeriod && owner.id) ? null : (
              <Menu
                className="menu"
                button={<DotsCircleIcon className="open-icon" />}
                closeButton={
                  <CloseIcon onClick={closeMenu} className="close-icon" />
                }
                customIconHeight="24px"
                menuId={`ownership-row-${owner.id}`}
                onClick={() => setMenuActive(true)}
              >
                <MenuContent>
                  <Typography
                    weight="700"
                    className="menu-item danger"
                    onClick={() => (request ? setNewSplit('0') : onRemove())}
                  >
                    <Translate id="RELEASE.NAMES_GENRES.MAIN_ARTISTS.LABEL.BUTTON.REMOVE" />
                  </Typography>
                </MenuContent>
              </Menu>
            )
          }
          customBorderRadius={
            owner.requestedSplit ||
            (request && !locked) ||
            (lockedPeriod && owner.ownerId === userId)
              ? '4px 0 0 0'
              : '4px 0 0 4px'
          }
          leftDecoration={leftDecoration}
          yellowLine={!!owner.requestedSplit || (request && !locked)}
          redLine={
            !!removedByUser && (!!owner.requestedSplit || (request && !locked))
          }
          redValue={
            !!removedByUser && (!!owner.requestedSplit || (request && !locked))
          }
        />
        <InputField
          style={{
            width: owner.requestedSplit || (request && !locked) ? '25%' : '23%',
            minWidth: '66px',
          }}
          className="input-split"
          label={
            owner.requestedSplit || (request && !lockedPeriod && !locked) ? (
              <Translate id="RELEASE.TRACK_INFO.TRACK.OWNERSHIP.INPUT.ORIGINAL_SPLIT" />
            ) : (
              <Translate id="RELEASE.TRACK_INFO.TRACK.OWNERSHIP.INPUT.SPLIT" />
            )
          }
          value={originalSplit}
          onChange={(value) => {
            if (handleChangeCondition(value)) {
              setOriginalSplit(value);
              (!request || (isRequest && lockedPeriod)) &&
                updateCurrentSplit(Number(value));
            }
          }}
          onFocus={() => setSplitFocus(true)}
          onBlur={() => setSplitFocus(false)}
          locked={
            owner.ownerType === 'RECORD_LABEL' ||
            owner.ownerType === 'MANAGER' ||
            (request && !lockedPeriod) ||
            (owner.hasOwnProperty('requestedSplit') && !locked) ||
            (lockedPeriod && !!owner.id && owner.ownerId !== userId)
          }
          customBorderRadius={
            (lockedPeriod && owner.ownerId === userId) ||
            owner.requestedSplit ||
            (request && !locked)
              ? '0'
              : '0 4px 4px 0'
          }
          redValue={
            !!removedByUser && (!!owner.requestedSplit || (request && !locked))
          }
        />
        {(owner.requestedSplit === 0 ||
          owner.requestedSplit ||
          (!lockedPeriod && request && !locked)) && (
          <InputField
            style={{ width: '25%' }}
            className="input-split"
            label={
              <Translate id="RELEASE.TRACK_INFO.TRACK.OWNERSHIP.INPUT.NEW_SPLIT" />
            }
            value={
              newSplit === '0' ? getNewSplitValue(owner, splitFocus) : newSplit
            }
            onChange={(value) => {
              if (handleChangeCondition(value)) {
                setNewSplit(value);
                !request || (owner.ownerId === userId && lockedPeriod)
                  ? updateCurrentSplit(Number(value))
                  : requestNewSplit(Number(value));
              }
            }}
            onFocus={() => setSplitFocus(true)}
            onBlur={() => setSplitFocus(false)}
            customBorderRadius={
              owner.requestedSplit || (request && !locked)
                ? '0 4px 0 0'
                : '0 4px 4px 0'
            }
            // TODO: DEVTEAM-831 - yellowValue integration need to be reviewed. This is just for demo.
            yellowValue={isRequest || !!givePercentUsername}
          />
        )}
      </Flex>
      {owner.split === 0 &&
        owner.ownerType !== 'RECORD_LABEL' &&
        !withoutGiveMessage && (
          <Message className={!!removedByUser ? 'red' : ''}>
            <Typography
              type="small"
              weight="700"
              color={!!removedByUser ? undefined : 'white'}
            >
              <Translate
                id={
                  !!removedByUser
                    ? 'OWNERSHIP.INPUT.REMOVED.MESSAGE'
                    : givePercentUsername
                    ? 'OWNERSHIP.INPUT.GIVE.MESSAGE'
                    : 'OWNERSHIP.INPUT.REQUEST.MESSAGE'
                }
                data={{
                  username: removedByUser || givePercentUsername,
                }}
              />
            </Typography>
          </Message>
        )}
    </OwnershipRowCtrlContainer>
  );
};

export default connect(
  null,
  mapDispatchToProps
)(SortableElement(OwnershipRowCtrl));

const OwnershipRowCtrlContainer = styled.div`
  position: relative;

  &:first-child {
    margin-top: 10px;
    ${({ theme }) => `@media screen and ${theme.device.laptopXL}`} {
      margin-top: 20px;
    }
  }

  .handle-icon {
    z-index: 9;
  }

  .menu {
    .menu-action {
      z-index: 0;
    }
    .open-icon,
    .close-icon {
      width: 24px;
      height: 24px;
      cursor: pointer;
    }
  }
  .decoration-right {
    z-index: 2;
  }
  .input-split {
    &.with-decoration {
      .decoration-right {
        z-index: 0;
      }
      &.parent-menu-open {
        .decoration-right {
          z-index: 2;
        }
      }
    }
  }

  .input-wrapper {
    position: relative;
    /* z-index: 5; */

    & > * {
      flex-grow: 1;
      flex-shrink: 1;
      flex-basis: 35%;

      &:first-child {
        flex-basis: 65%;
      }

      ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
        flex-basis: 30%;

        &:first-child {
          flex-basis: 70%;
        }
      }
    }
  }
`;

const Message = styled.div`
  background-color: ${({ theme }) => theme.colors.upload};
  padding: 10px 18px;
  border-radius: 0 0 4px 4px;
  margin-top: 3px;

  &.red {
    background-color: ${({ theme }) => theme.colors.pink};
  }
`;

const MenuContent = styled.div`
  display: grid;
  grid-gap: 5px;
  min-width: 128px;
  .menu-item {
    cursor: pointer;
    transition: color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
    &:hover {
      color: ${({ theme }) => theme.colors.tertiary};
    }
    &.danger {
      color: ${({ theme }) => theme.colors.accent};
      &:hover {
        color: ${({ theme }) => theme.colors.accentHover};
      }
    }
  }
`;
